import { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedThalamosUser,
  UserDeviceSession,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import {
  Button,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

function formatDate(value: string | null): string {
  return value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";
}

function UserDeviceSessionsPageInner({
  user,
  sessions,
  refetchSessions,
}: {
  user: ExtendedThalamosUser;
  sessions: UserDeviceSession[];
  refetchSessions: () => void;
}) {
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  return (
    <>
      <h3>
        User Devices / Sessions: {user.name} - {user.email} (
        <a href={routeFns.userProfilePage(user.id)}>profile</a>)
      </h3>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => setConfirmFn(null)}
        />
      )}
      <Box sx={{ mb: 2 }}>
        <Button
          color={"error"}
          label={"Revoke all (log out everywhere)"}
          // Enabled even with no active rows: revoke-all raises a user-level
          // watermark server-side that invalidates the user's existing tokens
          // regardless of what the device registry currently holds (e.g. mobile
          // may not have registered an x-device-id yet).
          onClick={() =>
            setConfirmFn({
              message: `Are you sure you want to log ${user.name} out of every device? They will need to sign in again on each device. This also revokes their Auth0 sessions and refresh tokens.`,
              // Run the async revoke, then close the modal in `finally` — so a
              // failure can't surface as an unhandled rejection and the dialog
              // doesn't linger open after the action completes.
              confirmFn: () => {
                void (async () => {
                  try {
                    const res = await api.users.revokeAllDeviceSessions(
                      user.id,
                    );
                    if (res.status === 204) {
                      renderSuccessToast({
                        message: "Successfully revoked all device sessions",
                      });
                      refetchSessions();
                    } else {
                      renderErrorToast({
                        message: "Failed to revoke all device sessions",
                      });
                    }
                  } catch {
                    renderErrorToast({
                      message: "Failed to revoke all device sessions",
                    });
                  } finally {
                    setConfirmFn(null);
                  }
                })();
              },
            })
          }
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Device</TableCell>
            <TableCell>Platform</TableCell>
            <TableCell>App version</TableCell>
            <TableCell>First seen</TableCell>
            <TableCell>Last seen</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                No device sessions recorded for this user
              </TableCell>
            </TableRow>
          ) : (
            sessions.map((session) => {
              const isRevoked = session.revokedAt !== null;
              return (
                <TableRow key={session.id}>
                  <TableCell>
                    {session.deviceName ?? session.deviceId}
                  </TableCell>
                  <TableCell>{session.platform ?? "-"}</TableCell>
                  <TableCell>{session.appVersion ?? "-"}</TableCell>
                  <TableCell>{formatDate(session.firstSeenAt)}</TableCell>
                  <TableCell>{formatDate(session.lastSeenAt)}</TableCell>
                  <TableCell>{isRevoked ? "Revoked" : "Active"}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </>
  );
}

export function UserDeviceSessionsPage({}: {}) {
  const { userId } = useParams();

  const [{ data: user, loading: userLoading }] =
    apiHooks.users.getUserViaPusher(userId!);

  const [{ data: sessions, loading: sessionsLoading }, refetchSessions] =
    apiHooks.users.getDeviceSessions(userId!);

  if (userLoading || sessionsLoading) {
    return <div />;
  }

  if (!user) {
    return (
      <Typography role="alert" color="error">
        This user cannot be shown.
      </Typography>
    );
  }

  return (
    <UserDeviceSessionsPageInner
      user={user}
      sessions={sessions ?? []}
      refetchSessions={refetchSessions}
    />
  );
}
